import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';

import type { Timeline } from '@src/editor/models/Timeline';

/**
 * PLAT-11073 — one spoken word of a Section, on the reel's own clock.
 *
 * Not held on {@link ReelSection}: a reel's transcript runs to thousands of
 * tokens, and a copy per Section would be the duplicated word array CLAUDE.md
 * warns about. Only the Section under the playhead is ever read out, so the
 * words are walked for that one Section, when it is the one being scrubbed.
 */
export type ReelSectionWord = {
  /** The word as it reads, with any punctuation that follows it attached. */
  value: string;
  /** Reel time — the same clock `ReelSection.startTime` is on. */
  startTime: number;
  endTime: number;
};

const isActive = (token: { state?: string }) =>
  token.state !== 'removed' && token.state !== 'cut';

/**
 * The words of one Section, in reading order, timed against the reel.
 *
 * `getActiveTranscriptElements` has already put each text token on the timeline
 * (element time + its relative offset), which is the clock the Section's own
 * `startTime` and the playhead are on — so a scrub position can be compared
 * against these directly, with no media-time conversion in between.
 */
export function reelSectionWords(
  timeline: Timeline,
  elementIds: readonly string[],
): ReelSectionWord[] {
  const track = timeline.tracks.find(
    (t): t is TranscriptTrack => t instanceof TranscriptTrack,
  );
  if (!track) return [];

  const elementsById = new Map(track.elements.map(el => [el.id, el]));
  const words: ReelSectionWord[] = [];

  for (const id of elementIds) {
    const element = elementsById.get(id);
    if (!element) continue;

    for (const token of element.getActiveTranscriptElements()) {
      if (!isActive(token)) continue;
      const value = token.value?.trim() ?? '';
      if (!value) continue;

      if (token.type !== 'text') {
        // Punctuation joins the word it follows. On its own it would take a
        // turn under the highlight as though it had been spoken, and it
        // carries no timing to be highlighted at.
        const previous = words[words.length - 1];
        if (previous) previous.value += value;
        continue;
      }

      // Typed nullable — a text token whose timing never survived an edit
      // cannot be placed on the strip, so it is left out rather than pinned
      // to zero.
      if (typeof token.ts !== 'number' || typeof token.end_ts !== 'number') {
        continue;
      }
      words.push({ value, startTime: token.ts, endTime: token.end_ts });
    }
  }

  return words;
}

/**
 * The word being spoken at a reel time: the last one to have started by then,
 * which holds the highlight through the silence after it rather than letting
 * the strip go blank between words.
 *
 * Binary search — this runs on every pointer move of a scrub, over a Section
 * that can hold hundreds of words.
 */
export function wordIndexAtTime(
  words: readonly ReelSectionWord[],
  time: number,
): number {
  let low = 0;
  let high = words.length - 1;
  let index = 0;

  while (low <= high) {
    const middle = (low + high) >> 1;
    if (words[middle].startTime <= time) {
      index = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return index;
}
